import ISLAND_POOL_FACTORY_ABI from 'abis/island-pool-factory.json'
import { WAGMI_REFETCH_POLICY_NEVER } from 'config/RefetchPolicies'
import { ISLAND_FACTORY_ADDRESSES } from 'constants/addresses'
import { useConnection } from 'hooks/useConnection'
import { useWriteContract } from 'hooks/useTransaction'
import { useCallback } from 'react'
import type { Hex } from 'viem'
import { Address } from 'viem'
import { useReadContract, useWaitForTransactionReceipt } from 'wagmi'

export interface DeployVaultParams {
  tokenA: string
  tokenB: string
  uniFee: number
  manager: string
  managerTreasury: string
  managerFee: number
  lowerTick: number
  upperTick: number
}

export function useKodiakVault() {
  const { chainId } = useConnection()
  const factoryAddress = chainId ? ISLAND_FACTORY_ADDRESSES[chainId] : undefined

  const { writeContractAsync, data: hash } = useWriteContract()
  const { isLoading: isConfirming } = useWaitForTransactionReceipt({
    hash: hash as Hex,
    chainId,
    query: {
      ...WAGMI_REFETCH_POLICY_NEVER, //it uses default pollingInterval internally, no need for us to refetch
    },
  })

  const deployVault = useCallback(
    async ({
      tokenA,
      tokenB,
      uniFee,
      manager,
      managerTreasury,
      managerFee,
      lowerTick,
      upperTick,
    }: DeployVaultParams) => {
      if (!factoryAddress) return null

      try {
        const hash = await writeContractAsync({
          address: factoryAddress,
          abi: ISLAND_POOL_FACTORY_ABI,
          functionName: 'deployVault',
          args: [tokenA, tokenB, uniFee, manager, managerTreasury, managerFee, lowerTick, upperTick],
          gas: BigInt(15_000_000),
        })

        return hash
      } catch (error) {
        console.error('Failed to deploy vault:', error)
        throw error
      }
    },
    [factoryAddress, writeContractAsync]
  )

  const { data: deployersData } = useReadContract({
    address: factoryAddress,
    abi: ISLAND_POOL_FACTORY_ABI,
    functionName: 'getDeployers',
    query: {
      ...WAGMI_REFETCH_POLICY_NEVER,
      enabled: !!factoryAddress,
    },
  })

  const getDeployers = useCallback(async () => {
    return (deployersData as Address[]) || []
  }, [deployersData])

  const getIslands = useCallback(async () => {
    if (!factoryAddress) return []
    // This would need to be called separately or use useReadContract with args
    // For now, returning empty array as this needs to be handled differently
    return []
  }, [factoryAddress])

  return {
    deployVault,
    getDeployers,
    getIslands,
    isConfirming,
  }
}
